import { AlertButton } from '@shared/alert/alertButton';
import { MENU_SEPARATOR, MenuItem, MenuList } from './menu.interfaces';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { UserUtils } from '@user/service/user-utils';
import { ProjectStatesDef } from '../../components/project/constants/project-states-def';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { escape } from 'lodash';
import { AlertHelper } from '@shared/alert/alert.helper';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { UserModel } from '@user/model/user.model';
import { ProjectService } from '../../components/import-export/service/project.service';
import { ProjectComponentPresenter } from '../../components/project-component.presenter';
import { ProjectRequestProcessor } from '../../components/request/processor/project-request.processor';
import { ProjectModel } from '../../model/project.model';
import { ElbridgeService } from '../../components/project/elbridge/service/elbridge.service';
import { IsProjectType } from '../../model/is-project-type';
import { Injectable } from '@angular/core';
import { SaveProjectService } from '../../components/project/project-save/save-project.service';
import { UserPermissionService } from '@user/service/user-permission.service';

@Injectable({providedIn: 'root'})
export class OrderMenuCreator {

  constructor(
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private projectRequestProcessor: ProjectRequestProcessor,
    private systemConfigurationService: SystemConfigurationService,
    private projectService: ProjectService,
    private elbridgeService: ElbridgeService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private saveProjectService: SaveProjectService,
    private userPermissionService: UserPermissionService,
  ) {}

  public create(): { id: string, content: MenuList } {
    return  { id: 'header-menu-order', content: this.getOrderItems() };
  }

  private getOrderItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.use_price_caculation',
        action: () => this.startPriceCalculation(),
        enabled: this.projectIsNotEmpty() && this.isPriceCalculationEnabled(),
        visible: this.projectIsNotEmpty() && this.isPriceCalculationVisible(),
        appActionLogId: 'header.menu.use_price_caculation',
        id: 'header-menu-project-price-calculation',
      },
      {
        label: 'header.menu.request_offer',
        action: () => this.startProjectRequest(),
        enabled: ProjectUtils.canRequestOffer(this.userModel.user, this.projectModel.currentProject),
        visible: !this.userModel.isConstruction,
        appActionLogId: 'header.menu.request_offer',
        id: 'header-menu-project-request-offer',
      },
      {
        label: 'header.menu.direct_order',
        action: () => this.directOrderProject(),
        enabled: this.isDirectOrderProjectEnabled(),
        visible: this.isDirectOrderProjectVisible(),
        appActionLogId: 'header.menu.direct_order',
        id: 'header-menu-project-direct-order',
      },
      this.elbridgeService.elbridgeHookExists ? this.getElbridgeHookMenuItem() : this.getElbridgeMenuItem(),
      {
        label: 'header.menu.eshop_watchlist',
        action: () => this.eShopWatchlistExport(),
        enabled: this.isEShopWatchlistExportEnabled(),
        visible: this.isEShopWatchlistExportVisible(),
        appActionLogId: 'header.menu.eshop_watchlist',
        id: 'header-menu-export-watchlist',
      },
      {
        label: 'header.menu.preferred_sample.title',
        action: () => this.determinePreferredSample(),
        enabled: this.isOrderingOfSamplesEnabled(),
        visible: this.isOrderingOfSamplesVisible(),
        appActionLogId: 'header.menu.order.preferred_sample',
        id: 'header-menu-order-preferred-sample',
      },
      {
        label: 'header.menu.configuration-consistent_sample',
        action: () => this.orderConfigurationConsistentSample(),
        enabled: this.isOrderingOfSamplesEnabled(),
        visible: this.isOrderingOfSamplesVisible(),
        appActionLogId: 'header.menu.configuration-consistent_sample',
        id: 'header-menu-project-configuration-consistent-sample',
      },
    ];
    if (this.isDiscontinuedProject()) {
      this.disableAllItems(items);
    }

    return items;
  }

  private async startPriceCalculation(): Promise<void> {
    if (await this.processNotDeliverableArticles()){
      return;
    }

    if (await this.processCustomArticles()){
      return;
    }

    await this.startPriceCalcRequest();
  }

  private async processNotDeliverableArticles(): Promise<boolean> {
    const articles = this.projectService.getArticleWithNotDeliveredStatus(this.projectModel.currentProject);
    if (articles.length <= 0) {
      return false;
    }

    await this.alertHelper.showAlert(
      'projectRequestPopup.title.infoCalculation',
      'projectRequestPopup.msg.notDeliverdArticles',
      { 0: escape(articles.map(a => a.articleNo).reduce((result, value) => result + '\n' + value)) },
      [AlertButton.OK],
      "info"
    );
    return true;
  }

  private async processCustomArticles(): Promise<boolean> {
    const customArticleNumbers: string[] = this.projectService.getCustomArticleNumbers(this.projectModel.currentProject);
    if (customArticleNumbers.length <= 0) {
      return false;
    }

    const answer = await this.alertHelper.showAlert(
      'projectRequestPopup.title.infoCalculation',
      'projectRequestPopup.msg.customArticle',
      { 0: escape(customArticleNumbers.reduce((result, value) => result + '\n' + value)) },
      [AlertButton.NO, AlertButton.YES],
      "info",
      { yesLabel: 'projectRequestPopup.button.pricecalculation', noLabel: 'projectRequestPopup.button.requestProject' }
    );

    if (answer === AlertButton.YES) {
      return false;
    } else if (answer === AlertButton.NO) {
      await this.startProjectRequest();
    }
    return true;
  }

  private async startPriceCalcRequest(): Promise<void> {
    if (await this.saveProjectService.saveProjectBeforeAction('main.message.calculation_save')) {
      this.projectComponentPresenter.openProjectRequestPriceCalculationDialog()
    }
  }

  private async startProjectRequest(): Promise<void> {
    if (await this.processNotDeliverableArticles()){
      return;
    }
    if (await this.saveProjectService.saveProjectBeforeAction('main.message.request_save')) {
      this.projectRequestProcessor.startProjectRequest();
    }
  }

  private isPriceCalculationEnabled(): boolean {
    if (!this.systemConfigurationService.currentConfiguration.priceCalculationActive) {
      return false;
    }

    if (
      !this.userModel.user ||
      !this.projectModel.currentProject ||
      ProjectUtils.isEmptyProject(this.projectModel.currentProject) ||
      this.projectModel.currentProject.finalizationState == null
    ) {
      return false;
    }

    return this.isPriceCalculationVisible();
  }

  private isPriceCalculationForbiddenByFinalizationState(): boolean {
    return (
      this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE ||
      this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE ||
      this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_ORDERED ||
      this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_FINALIZED_WITHOUT_PDM
    );
  }

  private isPriceCalculationVisible(): boolean {
    if (
      !UserUtils.isWagoUser(this.userModel.user) &&
      (this.isPriceCalculationForbiddenByFinalizationState() || StringUtils.isEmpty(this.userModel.user.addressNumber))
    ) {
      return false;
    }

    if (UserUtils.hasUseOnlinePriceCalculationPermission(this.userModel.user) &&
        (
          IsProjectType.visibleMountingRail(this.projectModel.currentProject) ||
          IsProjectType.freeMounting(this.projectModel.currentProject) ||
          (IsProjectType.mcsGds(this.projectModel.currentProject) && UserUtils.isWagoUser(this.userModel.user))
        )
      ) {
      return true;
    }

    return false;
  }

  private getElbridgeHookMenuItem(): MenuItem {
    return {
      label: 'header.menu.elbridge_start',
      action: () => this.openElbridgeDialog(),
      enabled: this.projectIsNotEmpty(),
      visible: this.isElbridgeVisible(),
      appActionLogId: 'header.menu.elbridge_start',
      id: 'header-menu-import-elbridge',
    };
  }

  private openElbridgeDialog(): void {
    this.projectComponentPresenter.openElbridgeDialog();
  }

  private projectIsNotEmpty(): boolean {
    return !ProjectUtils.isEmptyProject(this.projectModel.currentProject);
  }

  private isElbridgeVisible(): boolean {
    if (this.userModel.isConstruction) {
      return false;
    }
    return (
      IsProjectType.among(this.projectModel.currentProject, "FREE_MOUNTING", "XCOM", "MOUNTING_RAIL")
    );
  }

  private getElbridgeMenuItem(): MenuItem {
    return {
      label: 'header.menu.elbridge_start',
      action: () => this.openElbridgeInfo(),
      enabled: true,
      visible: this.isElbridgeVisible(),
      appActionLogId: 'header.menu.elbridge_info',
      id: 'header-menu-import-elbridge',
    };
  }

  private async openElbridgeInfo(): Promise<void> {
    if (await this.processNotDeliverableArticles()){
      return;
    }
    this.projectComponentPresenter.openElbridgeInfoDialog();
  }

  private async directOrderProject(): Promise<void> {
    if (await this.processNotDeliverableArticles()){
      return;
    }
    this.projectComponentPresenter.openProjectDirectOrderDialog();
  }

  private async orderConfigurationConsistentSample(): Promise<void> {
    if (await this.processNotDeliverableArticles()){
      return;
    }
    if (await this.saveProjectService.ensureProjectSaved()) {
      await this.projectComponentPresenter.showConfigurationConsistentSamples(this.projectModel.currentProject);
    }
  }

  private isDirectOrderProjectEnabled(): boolean {
    if (!this.systemConfigurationService.currentConfiguration.webshopActive || !this.isDirectOrderProjectVisible()) {
      return false;
    }
    if (!this.projectModel.currentProject || this.projectModel.isNewConfiguration || ProjectUtils.isEmptyProject(this.projectModel.currentProject)) {
      return false;
    } else {
      return true;
    }
  }

  private isDirectOrderProjectVisible(): boolean {
    return !this.userModel.isConstruction && this.userModel.user && this.userModel.user.hasPermission("ORDER_PROJECTS");
  }

  private isDiscontinuedProject(): boolean {
    return this.projectModel.currentProject && ArticleUtils.isStatusDiscontinued(this.projectModel.currentProject.materialStatus)
  }

  private disableAllItems(items: MenuList) {
    for (let item of items) {
      if (item !== MENU_SEPARATOR) {
        item.enabled = false;
      }
    }
  }

  private async eShopWatchlistExport(): Promise<void> {
    if (await this.saveProjectService.saveProjectBeforeAction('main.message.request_save')) {
      await this.projectComponentPresenter.openExportToWatchlistDialog()
    }
  }

  private isEShopWatchlistExportVisible(): boolean {
    const isCorrectProjectType = IsProjectType.visibleMountingRail(this.projectModel.currentProject)
      || IsProjectType.freeMounting(this.projectModel.currentProject);
    return isCorrectProjectType;
  }

  private isEShopWatchlistExportEnabled(): boolean {
    return this.userPermissionService.isLoggedIn();
  }

  private async determinePreferredSample() {
    if (await this.saveProjectService.ensureProjectSaved()) {
      this.projectComponentPresenter.showPreferredSamples(this.projectModel.currentProject);
    }
  }

  private isOrderingOfSamplesEnabled() {
    return this.userModel.user.hasPermission('SAVE_PROJECTS');
  }

  private isOrderingOfSamplesVisible(): boolean {
    return !this.userModel.isConstruction && IsProjectType.mcsGds(this.projectModel.currentProject);
  }
}
